import React, { FC, useEffect, useRef, useCallback, useState } from 'react';
import { Box, Typography, Switch, Collapse, Slider, IconButton, Select, MenuItem, Checkbox, ListItemText, FormControl } from '@mui/material';
import {
  Layers,
  CameraAlt as CameraAltIcon,
  GraphicEq as GraphicEqIcon,
  CalendarMonth as CalendarMonthIcon,
  ZoomIn as ZoomInIcon,
  Grass as GrassIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
} from '@mui/icons-material';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { mapBoxConfig } from 'src/mapbox-helpers/config';
import { Site } from 'src/api/Api';
import { ProjectWithSites } from 'src/state-management/state';
import { addSiteBoundaryWithColor } from 'src/view/InteractiveMap/map-helper';
import { getColorByIndex } from 'src/mapbox-helpers/color-utils';
import clientService from 'src/clientService';
import MapboxFeature from 'src/mapbox-helpers/MapboxFeature';
import { createRoot, Root } from 'react-dom/client';
import Tata3DCard from 'src/view/Project/components/Tata3DCard';

export interface DeploymentMarkerData {
  deployment_id?: string;
  location_id?: string;
  deployment_name?: string;
  status?: string;
  device_type?: string;
  site_id?: string;
  site_name?: string;
  total_files?: number;
  total_audiofiles?: number;
  [key: string]: any;
}

interface InteractiveMapProps {
  projectDetails: ProjectWithSites;
  sites: Site[];
  isBasemapOn: boolean;
  setIsBasemapOn: (on: boolean) => void;
  isFeatureLayerOn: boolean;
  setIsFeatureLayerOn: (on: boolean) => void;
  mapRef: React.MutableRefObject<mapboxgl.Map | null>;
  onDeviceClick: (siteName: string, markerData?: DeploymentMarkerData, deploymentId?: string) => void;
}

// Renders an SVG string onto a canvas and returns a Promise<ImageData> for Mapbox addImage
const createMarkerImageData = (svgString: string, size = 48): Promise<ImageData> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d')!;
    const img = new Image();
    const blob = new Blob([svgString], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    img.onload = () => {
      ctx.clearRect(0, 0, size, size);
      ctx.drawImage(img, 0, 0, size, size);
      URL.revokeObjectURL(url);
      resolve(ctx.getImageData(0, 0, size, size));
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });
};

// Circular badge style: dark disc + coloured glowing ring + white icon inside
const buildMarkerSVG = (ringColor: string, iconPath: string) => `
  <svg xmlns="http://www.w3.org/2000/svg" width="52" height="52" viewBox="0 0 52 52">
    <defs>
      <filter id="glow" x="-30%" y="-30%" width="160%" height="160%">
        <feGaussianBlur stdDeviation="3" result="blur"/>
        <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
      </filter>
    </defs>
    <!-- Outer glow ring -->
    <circle cx="26" cy="26" r="24" fill="none" stroke="${ringColor}" stroke-width="3" filter="url(#glow)" opacity="0.85"/>
    <!-- Dark disc background -->
    <circle cx="26" cy="26" r="21" fill="#0f0f1a" opacity="0.92"/>
    <!-- Inner ring -->
    <circle cx="26" cy="26" r="21" fill="none" stroke="${ringColor}" stroke-width="1.5" opacity="0.6"/>
    <!-- White icon centred -->
    <g transform="translate(12, 12) scale(1.16)" fill="white">
      ${iconPath}
    </g>
  </svg>
`;

// Audio waveform bars (like AudioMoth bioacoustic icon)
const AUDIO_ICON_PATH = `
  <rect x="1"  y="7"  width="2.5" height="10" rx="1.2"/>
  <rect x="5"  y="3"  width="2.5" height="18" rx="1.2"/>
  <rect x="9"  y="5"  width="2.5" height="14" rx="1.2"/>
  <rect x="13" y="1"  width="2.5" height="22" rx="1.2"/>
  <rect x="17" y="6"  width="2.5" height="12" rx="1.2"/>
  <rect x="21" y="9"  width="2.5" height="6"  rx="1.2"/>
`;

// Camera lens icon
const CAMERA_ICON_PATH = `
  <path d="M20 5h-2.17L16 3H8L6.17 5H4C2.9 5 2 5.9 2 7v12c0 1.1.9 2 2 2h16
    c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm-8 13c-2.76 0-5-2.24-5-5s2.24-5 5-5
    5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
`;


// Soil sensor icon (core sample)
const SOIL_SENSOR_ICON_PATH = `
  <path d="M7 2v20h10V2H7zm8 18H9v-4h6v4zm0-6H9v-4h6v4zm0-6H9V4h6v4z"/>
`;

const DEFAULT_ICON_PATH = `
  <circle cx="12" cy="12" r="8"/>
`;

const MULTIPLE_MARKER_PATH = `
  <path d="M14,11.5A2.5,2.5 0 0,0 16.5,9A2.5,2.5 0 0,0 14,6.5A2.5,2.5 0 0,0 11.5,9A2.5,2.5 0 0,0 14,11.5M14,2C17.86,2 21,5.13 21,9C21,14.25 14,22 14,22C14,22 7,14.25 7,9A7,7 0 0,1 14,2M5,9C5,13.5 10.08,19.66 11,20.81L10,22C10,22 3,14.25 3,9C3,5.83 5.11,3.15 8,2.29C6.16,3.94 5,6.33 5,9Z" />
`;

// Parsing WKT point coordinates [longitude, latitude]
const parseWKT = (wkt: string | null | undefined): [number, number] | null => {
  if (!wkt) return null;
  const match = wkt.match(/POINT\s*\(([^)]+)\)/i);
  if (match) {
    const coords = match[1].trim().split(/\s+/);
    if (coords.length === 2) {
      const lng = parseFloat(coords[0]);
      const lat = parseFloat(coords[1]);
      if (!isNaN(lng) && !isNaN(lat)) {
        return [lng, lat];
      }
    }
  }
  return null;
};
const TATA_ZONES = [
  "Deccan forest", "Grass land", "Narmada valley", "Savana ecosystem",
  "Trail plots", "Water margin", "Wetland forest", "Wildlife"
];

const TATA_ZONE_COLORS: Record<string, string> = {
  "Deccan forest": "#e6194b",
  "Grass land": "#3cb44b",
  "Narmada valley": "#4363d8",
  "Savana ecosystem": "#f58231",
  "Trail plots": "#42d4f4",
  "Water margin": "#f032e6",
  "Wetland forest": "#808000",
  "Wildlife": "#9a6324"
};

const InteractiveMap: FC<InteractiveMapProps> = ({
  projectDetails,
  sites,
  isBasemapOn,
  setIsBasemapOn,
  isFeatureLayerOn,
  setIsFeatureLayerOn,
  mapRef,
  onDeviceClick,
}) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const clickPopupNodeRef = useRef<HTMLDivElement | null>(null);
  const clickPopupRootRef = useRef<Root | null>(null);

  const fetchMarkersTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isMapLayersExpanded, setIsMapLayersExpanded] = useState(false);
  const [deploymentCycles, setDeploymentCycles] = useState<string[]>(['all']);
  const deploymentCyclesRef = useRef(deploymentCycles);
  const deploymentDatesMapRef = useRef<Map<string, string>>(new Map());

  const [isVisualExpanded, setIsVisualExpanded] = useState(false);
  const [isAcousticExpanded, setIsAcousticExpanded] = useState(false);
  const [isSoilExpanded, setIsSoilExpanded] = useState(false);

  const [showCamera, setShowCamera] = useState(true);
  const [showAquaticCamera, setShowAquaticCamera] = useState(true);
  const [showBioacoustic, setShowBioacoustic] = useState(true);
  const [showHydroacoustic, setShowHydroacoustic] = useState(true);
  const [showSoilSensor, setShowSoilSensor] = useState(true);
  const deviceFiltersRef = useRef({ camera: true, aquaticCamera: true, bioacoustic: true, hydroacoustic: true, soilSensor: true });
  
  const [availableDevices, setAvailableDevices] = useState({ camera: 0, aquaticCamera: 0, bioacoustic: 0, hydroacoustic: 0, soilSensor: 0 });
  const [siteSettings, setSiteSettings] = useState<Record<string, { visible: boolean; opacity: number }>>({});
  const [zoneSettings, setZoneSettings] = useState<Record<string, { visible: boolean; opacity: number }>>(() => {
    const initial: Record<string, { visible: boolean; opacity: number }> = {};
    TATA_ZONES.forEach(z => { initial[z] = { visible: true, opacity: 0.15 }; });
    return initial;
  });

  const isTataProject = !!projectDetails?.project_name?.toLowerCase().includes('tata');
  const [is3DActive, setIs3DActive] = useState<boolean>(true);
  const [exaggeration, setExaggeration] = useState<number>(1.0);
  const [textureOpacity, setTextureOpacity] = useState<number>(0.85);
  const [cursorElevation, setCursorElevation] = useState<number | null>(null);
  const is3DActiveRef = useRef(is3DActive);
  const exaggerationRef = useRef(exaggeration);

  const zoomToSite = useCallback((site: Site) => {
    if (!mapRef.current) return;
    const m = mapRef.current;
    const bounds = new mapboxgl.LngLatBounds();
    let hasCoords = false;
    
    if (site.boundary) {
      try {
        const geo = MapboxFeature.parseFeatureFromString(site.boundary, {});
        if (geo.geometry) {
          const geom = geo.geometry as any;
          const extendBoundsWithCoords = (val: any) => {
            if (Array.isArray(val) && typeof val[0] === 'number' && typeof val[1] === 'number') {
              bounds.extend(val as [number, number]);
              hasCoords = true;
            } else if (Array.isArray(val)) {
              val.forEach(extendBoundsWithCoords);
            }
          };
          extendBoundsWithCoords(geom.coordinates);
        }
      } catch (err) {
        console.error('Error parsing site boundary for zoom:', err);
      }
    } else {
      const centroid = parseWKT(site.centroid_wkt);
      if (centroid) {
        bounds.extend(centroid);
        hasCoords = true;
      }
    }
    
    if (hasCoords) {
      m.fitBounds(bounds, { padding: 40, maxZoom: 14, duration: 1000 });
    }
  }, [mapRef]);

  useEffect(() => {
    setSiteSettings(prev => {
      const newSettings = { ...prev };
      let changed = false;
      sites.forEach(site => {
        if (!site.site_id) return;
        if (!newSettings[site.site_id]) {
          newSettings[site.site_id] = { visible: true, opacity: 0.7 };
          changed = true;
        }
      });
      return changed ? newSettings : prev;
    });
  }, [sites]);

  useEffect(() => {
    if (!mapRef.current) return;
    const m = mapRef.current;
    
    Object.entries(siteSettings).forEach(([siteId, setting]) => {
      const fillLayerId = `fill-site-boundary-${siteId}`;
      if (m.getLayer(fillLayerId)) {
        m.setLayoutProperty(fillLayerId, 'visibility', setting.visible ? 'visible' : 'none');
        m.setPaintProperty(fillLayerId, 'fill-opacity', setting.visible ? setting.opacity : 0);
      }
    });
  }, [siteSettings]);

  useEffect(() => {
    if (!mapRef.current) return;
    const m = mapRef.current;
    
    if (m.getLayer('tata-motors-boundary-fill')) {
      const matchExpression: any[] = ['match', ['get', 'displayName']];
      const filterExpression: any[] = ['match', ['get', 'displayName']];
      let hasVisible = false;

      Object.entries(zoneSettings).forEach(([zoneName, setting]) => {
        if (setting.visible) {
          matchExpression.push(zoneName, setting.opacity);
          filterExpression.push(zoneName, true);
          hasVisible = true;
        }
      });
      matchExpression.push(0);
      filterExpression.push(false);

      if (!hasVisible) {
        m.setFilter('tata-motors-boundary-fill', ['==', 'displayName', 'none']);
        m.setFilter('tata-motors-boundary-line', ['==', 'displayName', 'none']);
      } else {
        m.setFilter('tata-motors-boundary-fill', filterExpression);
        m.setFilter('tata-motors-boundary-line', filterExpression);
        m.setPaintProperty('tata-motors-boundary-fill', 'fill-opacity', matchExpression as any);
      }
    }
  }, [zoneSettings]);

  useEffect(() => {
    const fetchDates = async () => {
      if (projectDetails?.project_id) {
        try {
          const res = await clientService.api.getProjectMapDataApiV1ProjectsProjectIdMapGet(projectDetails.project_id);
          const mapData = res.data?.deployments || [];
          const dateMap = new Map<string, string>();
          mapData.forEach((dep: any) => {
            if (dep.deployment_id && dep.start_date) {
              dateMap.set(dep.deployment_id, dep.start_date);
            }
          });
          deploymentDatesMapRef.current = dateMap;
        } catch (err) {
          console.error("Failed to fetch project deployment dates:", err);
        }
      }
    };
    fetchDates();
  }, [projectDetails?.project_id]);

  const fetchAndSetMarkers = useCallback(async (map: mapboxgl.Map, projectId: string) => {
    if (!map) return;
    const bounds = map.getBounds();
    if (!bounds) return;
    // Use a global bounding box so we fetch all markers for the project once.
    const bboxParam = `-180,-90,180,90`;

    try {
      console.log("[fetchAndSetMarkers] Querying bbox:", bboxParam);

      const response = await clientService.request<any[], any>({
        path: `/api/v1/projects/${projectId}/map/markers`,
        method: 'GET',
        query: { bbox: bboxParam, _t: Date.now() },
        format: 'json',
        secure: true,
      });

      let markers = response.data || [];
      console.log("[fetchAndSetMarkers] Returned markers:", markers);

      let cameraCount = 0;
      let aquaticCameraCount = 0;
      let bioCount = 0;
      let hydroCount = 0;
      let soilCount = 0;

      markers.forEach((m: any) => {
        const type = (m.device_type || 'default').toLowerCase();
        const dName = m.deployment_name;
        const dId = m.deployment_id;
        
        const isCamera = (type === 'camera' || dName === 'PMP-T0171' || dName === 'PMP-T0612') && dName !== 'PMP-T0352';
        const isAquaticCamera = type === 'aquatic_camera' || dName === 'PMP-T0352';
        const isBio = (type === 'audio' || type === 'bioacoustic') && dId !== '462fb893-ec5b-429e-a470-9744b0493ab9' && dId !== '6621d433-48bb-45a6-94b6-84948f136878';
        const isHydro = type === 'hydro' || type === 'hydroacoustic' || dId === '462fb893-ec5b-429e-a470-9744b0493ab9' || dId === '6621d433-48bb-45a6-94b6-84948f136878';
        const isSoil = type === 'soil_sensor' || type === 'soil';

        if (isCamera) cameraCount++;
        if (isAquaticCamera) aquaticCameraCount++;
        if (isBio) bioCount++;
        if (isHydro) hydroCount++;
        if (isSoil) soilCount++;
      });

      setAvailableDevices(prev => {
        if (prev.camera !== cameraCount || prev.aquaticCamera !== aquaticCameraCount || prev.bioacoustic !== bioCount || prev.hydroacoustic !== hydroCount || prev.soilSensor !== soilCount) {
          return { camera: cameraCount, aquaticCamera: aquaticCameraCount, bioacoustic: bioCount, hydroacoustic: hydroCount, soilSensor: soilCount };
        }
        return prev;
      });

      const cycles = deploymentCyclesRef.current;
      const filters = deviceFiltersRef.current;

      markers = markers.filter((m: any) => {
        const type = (m.device_type || 'default').toLowerCase();
        const dName = m.deployment_name;
        const dId = m.deployment_id;
        
        // Check if it's a camera
        const isCamera = (type === 'camera' || dName === 'PMP-T0171' || dName === 'PMP-T0612') && dName !== 'PMP-T0352';
        if (isCamera && !filters.camera) return false;

        // Check if it's an aquatic camera
        const isAquaticCamera = type === 'aquatic_camera' || dName === 'PMP-T0352';
        if (isAquaticCamera && !filters.aquaticCamera) return false;
        
        // Check if it's bioacoustic
        const isBio = (type === 'audio' || type === 'bioacoustic') && dId !== '462fb893-ec5b-429e-a470-9744b0493ab9' && dId !== '6621d433-48bb-45a6-94b6-84948f136878';
        if (isBio && !filters.bioacoustic) return false;
        
        // Check if it's hydroacoustic
        const isHydro = type === 'hydro' || type === 'hydroacoustic' || dId === '462fb893-ec5b-429e-a470-9744b0493ab9' || dId === '6621d433-48bb-45a6-94b6-84948f136878';
        if (isHydro && !filters.hydroacoustic) return false;

        // Check if it's soil sensor
        const isSoil = type === 'soil_sensor' || type === 'soil';
        if (isSoil && !filters.soilSensor) return false;

        return true;
      });

      if (!cycles.includes('all')) {
        const deploymentDateMap = deploymentDatesMapRef.current;

        markers = markers.filter((m: any) => {
          const startDate = m.start_date || deploymentDateMap.get(m.deployment_id);
          if (!startDate) return false;
          const d = new Date(startDate);

          let isMatch = false;
          if (cycles.includes('week1')) {
            const isLocalMatch = d.getFullYear() === 2026 && d.getMonth() === 7 && d.getDate() >= 12 && d.getDate() <= 21;
            const isUtcMatch = d.getUTCFullYear() === 2026 && d.getUTCMonth() === 7 && d.getUTCDate() >= 12 && d.getUTCDate() <= 21;
            if (isLocalMatch || isUtcMatch) isMatch = true;
          }
          if (cycles.includes('week2')) {
            const isLocalMatch = d.getFullYear() === 2026 && d.getMonth() === 8 && d.getDate() >= 21 && d.getDate() <= 28;
            const isUtcMatch = d.getUTCFullYear() === 2026 && d.getUTCMonth() === 8 && d.getUTCDate() >= 21 && d.getUTCDate() <= 28;
            if (isLocalMatch || isUtcMatch) isMatch = true;
          }
          if (cycles.includes('week3')) {
            const isLocalMatch = d.getFullYear() === 2026 && d.getMonth() === 9 && d.getDate() >= 1 && d.getDate() <= 7;
            const isUtcMatch = d.getUTCFullYear() === 2026 && d.getUTCMonth() === 9 && d.getUTCDate() >= 1 && d.getUTCDate() <= 7;
            if (isLocalMatch || isUtcMatch) isMatch = true;
          }
          if (cycles.includes('week4')) {
            const isLocalMatch = d.getFullYear() === 2026 && d.getMonth() === 9 && d.getDate() >= 12 && d.getDate() <= 19;
            const isUtcMatch = d.getUTCFullYear() === 2026 && d.getUTCMonth() === 9 && d.getUTCDate() >= 12 && d.getUTCDate() <= 19;
            if (isLocalMatch || isUtcMatch) isMatch = true;
          }
          return isMatch;
        });
      }



      // Count markers at exact coordinates to flag multiples
      const coordsCount = new Map<string, number>();
      markers.forEach((m: any) => {
        const key = `${m.longitude},${m.latitude}`;
        coordsCount.set(key, (coordsCount.get(key) || 0) + 1);
      });

      const groupedMarkers = new Map<string, any[]>();
      markers.forEach((m: any) => {
        const key = `${m.longitude},${m.latitude}`;
        if (!groupedMarkers.has(key)) {
           groupedMarkers.set(key, []);
        }
        groupedMarkers.get(key)!.push(m);
      });

      const features = Array.from(groupedMarkers.values()).map((group: any[]) => {
        const marker = group[0];
        return {
          type: 'Feature' as const,
          geometry: {
            type: 'Point' as const,
            coordinates: [marker.longitude, marker.latitude],
          },
          properties: {
            ...marker,
            deployment_id: marker.deployment_id,
            location_id: marker.location_id,
            deployment_name: marker.deployment_name,
            status: marker.status,
            device_type: marker.device_type,
            site_id: marker.site_id,
            site_name: marker.site_name,
            total_files: marker.total_files ?? marker.total_audiofiles ?? 0,
            is_multiple: group.length > 1,
            deployments_json: group.length > 1 ? JSON.stringify(group) : undefined,
          },
        };
      });

      const source = map.getSource('deployment-markers') as mapboxgl.GeoJSONSource;
      if (source) {
        source.setData({
          type: 'FeatureCollection',
          features: features,
        });
      }
    } catch (e) {
      console.error('Error fetching map markers:', e);
    }
  }, []);

  const triggerFetchMarkers = useCallback((map: mapboxgl.Map, projectId: string) => {
    if (fetchMarkersTimeoutRef.current) {
      clearTimeout(fetchMarkersTimeoutRef.current);
    }
    fetchMarkersTimeoutRef.current = setTimeout(() => {
      fetchAndSetMarkers(map, projectId);
    }, 300);
  }, [fetchAndSetMarkers]);

  useEffect(() => {
    deploymentCyclesRef.current = deploymentCycles;
    deviceFiltersRef.current = { camera: showCamera, aquaticCamera: showAquaticCamera, bioacoustic: showBioacoustic, hydroacoustic: showHydroacoustic, soilSensor: showSoilSensor };
    if (mapRef.current && projectDetails?.project_id) {
      triggerFetchMarkers(mapRef.current, projectDetails.project_id);
    }
  }, [deploymentCycles, showCamera, showAquaticCamera, showBioacoustic, showHydroacoustic, showSoilSensor, projectDetails?.project_id, mapRef, triggerFetchMarkers]);

  // Initialize Mapbox gl
  useEffect(() => {
    if (!mapContainer.current) return;
    if (!projectDetails) return;

    mapboxgl.accessToken = mapBoxConfig.accessToken;
    const m = new mapboxgl.Map({
      container: mapContainer.current,
      style: isBasemapOn ? 'mapbox://styles/mapbox/satellite-v9' : 'mapbox://styles/mapbox/dark-v11',
      center: [78.9629, 20.5937],
      zoom: 4,
      attributionControl: false,
      maxPitch: 85,
    });
    
    // Prevent unhandled mapbox tile errors from crashing the React app
    m.on('error', (e) => {
      console.warn('Mapbox GL JS error caught:', e?.error?.message || e?.error || e);
    });

    mapRef.current = m;

    const resizeObserver = new ResizeObserver(() => {
      if (m) {
          m.resize();
      }
    });
    resizeObserver.observe(mapContainer.current);

    m.addControl(new mapboxgl.NavigationControl({ visualizePitch: true }), 'bottom-right');

    const hoverPopup = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
      className: 'site-hover-popup',
    });

    const clickPopup = new mapboxgl.Popup({
      closeButton: true,
      closeOnClick: true,
      className: 'site-click-popup',
    });

    m.on('load', async () => {
      m.setFog({
        color: 'rgb(22, 28, 45)',
        'high-color': 'rgb(12, 16, 26)',
        'horizon-blend': 0.03,
        'space-color': 'rgb(11, 11, 25)',
        'star-intensity': 0.6,
      });

      const bounds = new mapboxgl.LngLatBounds();
      let parseCoordsCount = 0;

      sites.forEach((site, index) => {
        // Draw Polygon boundary
        if (site.boundary) {
          try {
            addSiteBoundaryWithColor(m, site, getColorByIndex(index), {
              onMouseEnter: (e) => {
                m.getCanvas().style.cursor = 'pointer';
                if (e.features && e.features.length > 0) {
                  const props = e.features[0].properties;
                  hoverPopup
                    .setLngLat(e.lngLat)
                    .setHTML(
                      `
                      <div style="color:#fff;font-size:11px;padding:2px;">
                        <strong>${props?.Name || ''}</strong><br/>
                        Area: ${props?.Area || 0} ha
                      </div>
                    `,
                    )
                    .addTo(m);
                }
              },
              onMouseLeave: () => {
                m.getCanvas().style.cursor = '';
                hoverPopup.remove();
              },
            });
          } catch (err) {
            console.error('Error rendering site boundary:', err);
          }
        }
        if (site.boundary) {
          try {
            const geo = MapboxFeature.parseFeatureFromString(site.boundary, {});
            if (geo.geometry) {
              const geom = geo.geometry as any;
              const extendBoundsWithCoords = (val: any) => {
                if (Array.isArray(val) && typeof val[0] === 'number' && typeof val[1] === 'number') {
                  bounds.extend(val as [number, number]);
                  parseCoordsCount++;
                } else if (Array.isArray(val)) {
                  val.forEach(extendBoundsWithCoords);
                }
              };
              extendBoundsWithCoords(geom.coordinates);
            }
          } catch (err) {
            console.error('Error parsing site boundary for bounds:', err);
          }
        } else {
          const centroid = parseWKT(site.centroid_wkt);
          if (centroid) {
            bounds.extend(centroid);
            parseCoordsCount++;
          }
        }
      });

      // Register custom marker icons in Mapbox (must await async image loading)
      const markerDefs = [
        { id: 'marker-audio', color: '#a855f7', icon: AUDIO_ICON_PATH },
        { id: 'marker-camera', color: '#ef4444', icon: CAMERA_ICON_PATH },
        { id: 'marker-hydro', color: '#3b82f6', icon: AUDIO_ICON_PATH },
        { id: 'marker-soil_sensor', color: '#eab308', icon: SOIL_SENSOR_ICON_PATH },
        { id: 'marker-default', color: '#11b4da', icon: DEFAULT_ICON_PATH },
        { id: 'marker-multiple', color: '#f59e0b', icon: MULTIPLE_MARKER_PATH },
        { id: 'marker-pmp-t0171', color: '#10b981', icon: CAMERA_ICON_PATH },
        { id: 'marker-pmp-t0612', color: '#10b981', icon: CAMERA_ICON_PATH },
        { id: 'marker-pmp-t0352', color: '#3b82f6', icon: CAMERA_ICON_PATH },
      ];
      await Promise.all(
        markerDefs.map(async ({ id, color, icon }) => {
          if (!m.hasImage(id)) {
            const svg = buildMarkerSVG(color, icon);
            const imageData = await createMarkerImageData(svg, 52);
            m.addImage(id, imageData, { pixelRatio: 2 });
          }
        })
      );

      // Add deployment markers source with clustering enabled
      m.addSource('deployment-markers', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
        cluster: true,
        clusterMaxZoom: 14,
        clusterRadius: 50,
      });

      // Add cluster circle layer
      m.addLayer({
        id: 'clusters',
        type: 'circle',
        source: 'deployment-markers',
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': [
            'step',
            ['get', 'point_count'],
            '#51bbd6',
            10,
            '#f1f075',
            30,
            '#f28cb1',
          ],
          'circle-radius': [
            'step',
            ['get', 'point_count'],
            20,
            10,
            30,
            30,
            40,
          ],
          'circle-stroke-width': 1,
          'circle-stroke-color': '#fff',
        },
      });

      // Add cluster count labels layer
      m.addLayer({
        id: 'cluster-count',
        type: 'symbol',
        source: 'deployment-markers',
        filter: ['has', 'point_count'],
        layout: {
          'text-field': '{point_count}',
          'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
          'text-size': 12,
        },
      });
      // Add unclustered point layer using custom SVG pin icons
      m.addLayer({
        id: 'unclustered-point',
        type: 'symbol',
        source: 'deployment-markers',
        filter: ['!', ['has', 'point_count']],
        layout: {
          'icon-image': [
            'case',
            ['==', ['get', 'is_multiple'], true], 'marker-multiple',
            ['==', ['get', 'deployment_id'], '462fb893-ec5b-429e-a470-9744b0493ab9'], 'marker-hydro',
            ['==', ['get', 'deployment_id'], '6621d433-48bb-45a6-94b6-84948f136878'], 'marker-hydro',
            ['==', ['get', 'deployment_name'], 'PMP-T0171'], 'marker-pmp-t0171',
            ['==', ['get', 'deployment_name'], 'PMP-T0612'], 'marker-pmp-t0612',
            ['==', ['get', 'deployment_name'], 'PMP-T0352'], 'marker-pmp-t0352',
            [
              'match',
              ['downcase', ['coalesce', ['get', 'device_type'], 'default']],
              'audio', 'marker-audio',
              'bioacoustic', 'marker-audio',
              'camera', 'marker-camera',
              'hydro', 'marker-hydro',
              'soil_sensor', 'marker-soil_sensor',
              'soil', 'marker-soil_sensor',
              'marker-default',
            ]
          ],
          'icon-size': [
            'case',
            ['==', ['get', 'is_multiple'], true], 1.1,
            0.85
          ],
          'icon-anchor': 'center',
          'icon-allow-overlap': true,
        },
      });
      // Handle cluster expansions on click
      m.on('click', 'clusters', (e) => {
        const features = m.queryRenderedFeatures(e.point, {
          layers: ['clusters'],
        });
        if (!features.length) return;
        const clusterId = features[0].properties?.cluster_id;
        const source = m.getSource('deployment-markers') as mapboxgl.GeoJSONSource;
        source.getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err || zoom === undefined || zoom === null) return;
          const coordinates = (features[0].geometry as any).coordinates;
          m.easeTo({
            center: coordinates,
            zoom: zoom,
          });
        });
      });

      // Handle marker click to open data panel / audio player
      m.on('click', 'unclustered-point', (e) => {
        const bbox: [mapboxgl.PointLike, mapboxgl.PointLike] = [
          [e.point.x - 5, e.point.y - 5],
          [e.point.x + 5, e.point.y + 5]
        ];
        const features = m.queryRenderedFeatures(bbox, {
          layers: ['unclustered-point'],
        });
        if (features.length === 0) return;

        // Extract deployments
        const allDeployments: any[] = [];
        features.forEach(f => {
          if (f.properties?.deployments_json) {
             try {
               allDeployments.push(...JSON.parse(f.properties.deployments_json));
             } catch (e) {
               console.error("Failed to parse deployments_json", e);
             }
          } else {
             allDeployments.push(f.properties);
          }
        });
        const uniqueFeatures = Array.from(new Map(allDeployments.map(d => [d.deployment_id, d])).values());

        const coordinates = (features[0].geometry as any).coordinates.slice();
        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
          coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
        }

        if (uniqueFeatures.length === 1) {
          const properties = uniqueFeatures[0];
          if (!properties) return;

          const siteName = properties.site_name;

          onDeviceClick(siteName, properties as DeploymentMarkerData, properties?.deployment_id);
        } else {
          if (!clickPopupNodeRef.current) {
            clickPopupNodeRef.current = document.createElement('div');
          }
          if (!clickPopupRootRef.current) {
            clickPopupRootRef.current = createRoot(clickPopupNodeRef.current);
          }

          clickPopupRootRef.current.render(
            <>
              <style>
                {`
                  .site-click-popup .mapboxgl-popup-content {
                    background-color: #000;
                    padding: 12px;
                    border-radius: 8px;
                    border: 1px solid #333;
                  }
                  .site-click-popup .mapboxgl-popup-tip {
                    border-top-color: #000;
                    border-bottom-color: #000;
                  }
                  .site-click-popup .mapboxgl-popup-close-button {
                    color: #fff;
                    font-size: 16px;
                  }
                `}
              </style>
              <div style={{ display: 'flex', flexDirection: 'row', gap: '8px', alignItems: 'center', marginTop: '12px' }}>
                {uniqueFeatures.map((p, i) => {
                  const type = p?.device_type?.toLowerCase() || '';
                  const isCam = type === 'camera';
                  const isSoil = type === 'soil_sensor' || type === 'soil';
                  const Icon = isCam ? CameraAltIcon : isSoil ? GrassIcon : GraphicEqIcon;
                  const color = isCam ? '#ef4444' : type === 'hydro' ? '#3b82f6' : isSoil ? '#eab308' : '#22c55e';

                  return (
                    <div
                      key={i}
                      onClick={() => {
                        onDeviceClick(p?.site_name, p as DeploymentMarkerData, p?.deployment_id);
                        clickPopup.remove();
                      }}
                      style={{
                        background: 'rgba(255,255,255,0.1)',
                        padding: '6px 10px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        color: '#fff',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        transition: 'background 0.2s',
                        border: '1px solid rgba(255,255,255,0.1)'
                      }}
                      onMouseOver={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.2)')}
                      onMouseOut={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.1)')}
                    >
                      <Icon sx={{ fontSize: 18, color }} />
                      {p?.deployment_name || 'Deployment'}
                    </div>
                  );
                })}
              </div>
            </>
          );

          hoverPopup.remove();
          clickPopup
            .setLngLat(coordinates)
            .setDOMContent(clickPopupNodeRef.current)
            .addTo(m);
        }
      });

      // Pointer cursors and hover popups on hover
      m.on('mouseenter', 'clusters', () => {
        m.getCanvas().style.cursor = 'pointer';
      });
      m.on('mouseleave', 'clusters', () => {
        m.getCanvas().style.cursor = '';
      });

      m.on('mouseenter', 'unclustered-point', (e) => {
        m.getCanvas().style.cursor = 'pointer';

        const bbox: [mapboxgl.PointLike, mapboxgl.PointLike] = [
          [e.point.x - 5, e.point.y - 5],
          [e.point.x + 5, e.point.y + 5]
        ];
        const features = m.queryRenderedFeatures(bbox, { layers: ['unclustered-point'] });
        if (!features.length) return;

        const allDeployments: any[] = [];
        features.forEach(f => {
          if (f.properties?.deployments_json) {
             try {
               allDeployments.push(...JSON.parse(f.properties.deployments_json));
             } catch (e) { /* ignore */ }
          } else {
             allDeployments.push(f.properties);
          }
        });
        const uniqueFeatures = Array.from(new Map(allDeployments.map(d => [d.deployment_id, d])).values());

        const coordinates = (features[0].geometry as any).coordinates.slice();
        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
          coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
        }

        const text = uniqueFeatures.length === 1
          ? (uniqueFeatures[0]?.deployment_name || 'Deployment')
          : 'Multiple Deployments';

        hoverPopup
          .setLngLat(coordinates)
          .setHTML(
            `<div style="color:#fff;font-size:11px;padding:2px;">
              <strong>${text}</strong>
            </div>`
          )
          .addTo(m);
      });

      m.on('mouseleave', 'unclustered-point', () => {
        m.getCanvas().style.cursor = '';
        hoverPopup.remove();
      });

      // Removed moveend boundary fetching to keep all markers visible regardless of zoom/pan

      // Load Tata Motors Boundary if project name includes 'tata'
      if (projectDetails?.project_name?.toLowerCase().includes('tata')) {
        m.addSource('tata-motors-boundary', {
          type: 'geojson',
          data: '/TataMotors_Pimpri_EMU_Boundaries.geojson'
        });

        m.addLayer({
          id: 'tata-motors-boundary-fill',
          type: 'fill',
          source: 'tata-motors-boundary',
          paint: {
            'fill-color': ['get', 'color'],
            'fill-opacity': 0.15
          }
        }, 'clusters');

        m.addLayer({
          id: 'tata-motors-boundary-line',
          type: 'line',
          source: 'tata-motors-boundary',
          paint: {
            'line-color': ['get', 'color'],
            'line-width': 2,
            'line-opacity': 0.8
          }
        }, 'clusters');

        m.on('mousemove', 'tata-motors-boundary-fill', (e) => {
          if (e.features && e.features.length > 0) {
            m.getCanvas().style.cursor = 'pointer';
            const feature = e.features[0];
            const displayName = feature.properties?.displayName || feature.properties?.display_name || 'Boundary';
            
            hoverPopup
              .setLngLat(e.lngLat)
              .setHTML(
                `<div style="color:#fff;font-size:11px;padding:2px;">
                  <strong>${displayName}</strong>
                </div>`
              )
              .addTo(m);
          }
        });

        m.on('mouseleave', 'tata-motors-boundary-fill', () => {
          m.getCanvas().style.cursor = '';
          hoverPopup.remove();
        });

        // 1) Tata Motors 3D DSM Terrain Source (512px Terrain-RGB)
        if (!m.getSource('tata-motors-dem')) {
          m.addSource('tata-motors-dem', {
            type: 'raster-dem',
            tiles: [`${window.location.origin}/dem/{z}/{x}/{y}.png`],
            tileSize: 512,
            minzoom: 12,
            maxzoom: 18,
            bounds: [73.805459, 18.6340277, 73.8225444, 18.6511067],
            encoding: 'mapbox',
          });
        }

        // 2) Tata Motors Drone DSM Colour-Relief Texture Source & Layer
        if (!m.getSource('tata-motors-texture')) {
          m.addSource('tata-motors-texture', {
            type: 'raster',
            tiles: [`${window.location.origin}/texture/{z}/{x}/{y}.png`],
            tileSize: 512,
            minzoom: 12,
            maxzoom: 18,
            bounds: [73.805459, 18.6340277, 73.8225444, 18.6511067],
          });

          m.addLayer({
            id: 'tata-motors-texture-layer',
            type: 'raster',
            source: 'tata-motors-texture',
            paint: {
              'raster-opacity': 0.85,
              'raster-fade-duration': 0,
            },
          }, 'tata-motors-boundary-fill');
        }

        // 3) Synchronize 3D Terrain mesh when camera is over survey bounds
        const [w, s, eBounds, n] = [73.805459, 18.6340277, 73.8225444, 18.6511067];
        const M = 0.01;
        const syncTerrain = () => {
          if (!mapRef.current) return;
          const mapInstance = mapRef.current;
          if (!is3DActiveRef.current) {
            mapInstance.setTerrain(null);
            return;
          }
          const c = mapInstance.getCenter();
          const onSite = mapInstance.getZoom() >= 12 && c.lng > w - M && c.lng < eBounds + M && c.lat > s - M && c.lat < n + M;
          if (onSite && mapInstance.getSource('tata-motors-dem')) {
            mapInstance.setTerrain({ source: 'tata-motors-dem', exaggeration: exaggerationRef.current });
          } else {
            mapInstance.setTerrain(null);
          }
        };

        syncTerrain();
        m.on('moveend', syncTerrain);

        // 4) Track cursor elevation within the survey footprint
        let lastMove = 0;
        const handleElevationMove = (e: mapboxgl.MapMouseEvent) => {
          const now = performance.now();
          if (now - lastMove < 60) return;
          lastMove = now;

          if (!is3DActiveRef.current) {
            setCursorElevation(null);
            return;
          }
          try {
            const h = m.queryTerrainElevation(e.lngLat, { exaggerated: false });
            if (h != null && !isNaN(h)) {
              setCursorElevation(h);
            } else {
              setCursorElevation(null);
            }
          } catch {
            setCursorElevation(null);
          }
        };

        m.on('mousemove', handleElevationMove);
        m.on('mouseout', () => setCursorElevation(null));
      }


      // Load initial markers
      const pId = projectDetails?.project_id;
      if (pId) {
        triggerFetchMarkers(m, pId);
      }

      if (parseCoordsCount > 0 && !bounds.isEmpty()) {
        setTimeout(() => {
          if (m) {
            m.resize();
            try {
              m.fitBounds(bounds, { padding: 40, maxZoom: 12, duration: 1000 });
            } catch (err) {
              console.warn("fitBounds failed on load:", err);
            }
          }
        }, 200);
      }
    });

    return () => {
      resizeObserver.disconnect();
      hoverPopup.remove();
      m.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectDetails?.project_id, sites, isBasemapOn, triggerFetchMarkers]);

  // Sync 3D Terrain & Exaggeration
  useEffect(() => {
    is3DActiveRef.current = is3DActive;
    exaggerationRef.current = exaggeration;
    const m = mapRef.current;
    if (!m || !isTataProject) return;

    if (m.getSource('tata-motors-dem')) {
      if (is3DActive) {
        m.setTerrain({ source: 'tata-motors-dem', exaggeration });
        m.setFog({
          range: [1, 12],
          color: isBasemapOn ? '#dfe6ea' : '#111416',
          'horizon-blend': 0.08,
        });
      } else {
        m.setTerrain(null);
        m.setFog(null);
      }
    }

    if (m.getLayer('tata-motors-texture-layer')) {
      m.setLayoutProperty('tata-motors-texture-layer', 'visibility', is3DActive ? 'visible' : 'none');
    }
  }, [is3DActive, exaggeration, isTataProject, isBasemapOn, mapRef]);

  // Sync Texture Opacity
  useEffect(() => {
    const m = mapRef.current;
    if (!m || !isTataProject) return;

    if (m.getLayer('tata-motors-texture-layer')) {
      m.setPaintProperty('tata-motors-texture-layer', 'raster-opacity', textureOpacity);
    }
  }, [textureOpacity, isTataProject, mapRef]);

  const handleFlyToSite = useCallback(() => {
    if (!mapRef.current) return;
    setIs3DActive(true);
    mapRef.current.flyTo({
      center: [73.8140017, 18.6425672],
      zoom: 16.2,
      pitch: 62,
      bearing: -25,
      duration: 2500,
      essential: true,
    });
  }, [mapRef]);

  const handleReset2D = useCallback(() => {
    if (!mapRef.current) return;
    mapRef.current.flyTo({
      pitch: 0,
      bearing: 0,
      duration: 1500,
      essential: true,
    });
  }, [mapRef]);

  return (
    <Box className="center-panel">
      <Box ref={mapContainer} className="mapbox-container-main" />

      {/* Overlays Container */}
      <Box sx={{ 
        position: 'absolute', 
        top: 24, 
        left: 24, 
        display: 'flex', 
        flexDirection: 'column', 
        gap: 1, 
        zIndex: 10,
        maxHeight: 'calc(100% - 48px)',
        overflowY: 'auto',
        overflowX: 'hidden',
        paddingRight: '8px',
        '&::-webkit-scrollbar': { width: '4px' },
        '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: '4px' },
      }}>
        {/* Card 1: Map Layers & Master Toggle */}
        <Box
          className="glass-panel"
          sx={{
            width: isMapLayersExpanded ? '230px' : '48px',
            minHeight: '48px',
            padding: isMapLayersExpanded ? '16px' : '0',
            borderRadius: isMapLayersExpanded ? '12px' : '24px',
            transition: 'all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
            background: isMapLayersExpanded ? 'rgba(10, 11, 14, 0.75)' : 'rgba(20, 22, 28, 0.85)',
            boxShadow: 'none',
            border: '1px solid rgba(255,255,255,0.08)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            '&:hover': {
              background: isMapLayersExpanded ? 'rgba(10, 11, 14, 0.75)' : 'rgba(30, 34, 43, 0.9)',
            }
          }}
        >
          <Box
            onClick={() => setIsMapLayersExpanded(!isMapLayersExpanded)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: isMapLayersExpanded ? '16px' : 0,
              cursor: 'pointer',
              height: isMapLayersExpanded ? '24px' : '48px',
              justifyContent: isMapLayersExpanded ? 'flex-start' : 'center',
              transition: 'all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#10B981',
                width: isMapLayersExpanded ? 'auto' : '100%',
                height: isMapLayersExpanded ? 'auto' : '100%',
                transition: 'all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
                mr: isMapLayersExpanded ? 1.5 : 0,
              }}
            >
              <Layers sx={{ fontSize: isMapLayersExpanded ? 18 : 22, transition: 'all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)' }} />
            </Box>
            
            <Typography
              sx={{
                fontSize: '11px',
                fontWeight: 700,
                color: '#10B981',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                whiteSpace: 'nowrap',
                opacity: isMapLayersExpanded ? 1 : 0,
                transform: isMapLayersExpanded ? 'translateX(0)' : 'translateX(-10px)',
                transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                overflow: 'hidden',
                m: 0,
                display: isMapLayersExpanded ? 'block' : 'none',
              }}
            >
              Map Layers
            </Typography>
          </Box>

          <Collapse in={isMapLayersExpanded}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.85)', fontWeight: 600 }}>Basemaps</Typography>
                <Switch 
                  size="small" 
                  checked={isBasemapOn} 
                  onChange={(e) => setIsBasemapOn(e.target.checked)} 
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#10B981' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#10B981' },
                  }}
                />
              </Box>
            </Box>
          </Collapse>
        </Box>

        {/* Additional Cards (Synced Bounce) */}
        <Collapse 
          in={isMapLayersExpanded} 
          timeout={600}
          sx={{ 
            width: isMapLayersExpanded ? '230px' : '48px',
            transition: 'width 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
            '& .MuiCollapse-wrapperInner': {
              transitionTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1) !important'
            }
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* Card 4: Site Layers */}
            <Box
              className="glass-panel"
              sx={{
                width: '100%',
                padding: '16px',
                borderRadius: isMapLayersExpanded ? '12px' : '24px',
                background: 'rgba(10, 11, 14, 0.75)',
                boxShadow: 'none',
                border: '1px solid rgba(255,255,255,0.08)',
                transform: isMapLayersExpanded ? 'translateY(0)' : 'translateY(-24px)',
                opacity: isMapLayersExpanded ? 1 : 0,
                transition: 'all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
                overflow: 'hidden',
              }}
            >
              <Typography
                sx={{
                  fontSize: '10px',
                  fontWeight: 700,
                  color: 'rgba(255,255,255,0.5)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  display: 'flex',
                  alignItems: 'center',
                  mb: 1.5
                }}
              >
                <Layers sx={{ fontSize: 13, mr: 0.75, color: 'rgba(255,255,255,0.5)' }} />
                Site Layers
              </Typography>

              <Box 
                sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: 1.5, 
                  maxHeight: '200px', 
                  overflowY: 'auto', 
                  overflowX: 'hidden',
                  '&::-webkit-scrollbar': { display: 'none' }, 
                  msOverflowStyle: 'none',
                  scrollbarWidth: 'none',
                }}
              >
                {sites.map((site) => {
                  if (!site.site_id) return null;
                  const setting = siteSettings[site.site_id] || { visible: true, opacity: 0.7 };
                  return (
                    <Box key={site.site_id} sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography sx={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.85)', fontWeight: 500 }}>
                          {site.site_name}
                        </Typography>
                        <Switch
                          size="small"
                          checked={setting.visible}
                          onChange={(e) => setSiteSettings(prev => ({
                            ...prev,
                            [site.site_id as string]: { ...setting, visible: e.target.checked }
                          }))}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': { color: '#10B981' },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#10B981' },
                          }}
                        />
                      </Box>
                      <Collapse in={setting.visible}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5 }}>
                          <Slider
                            size="small"
                            value={setting.opacity}
                            min={0}
                            max={1}
                            step={0.01}
                            onChange={(e, val) => setSiteSettings(prev => ({
                              ...prev,
                              [site.site_id as string]: { ...setting, opacity: val as number }
                            }))}
                            sx={{
                              color: '#10B981',
                              ml: '-6px', // Shift left by half thumb size so the track aligns with text above
                              width: 'calc(100% - 24px)', // Adjust width slightly due to margin shift
                              '& .MuiSlider-thumb': { width: 12, height: 12 },
                              '& .MuiSlider-track': { border: 'none' },
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => zoomToSite(site)}
                            sx={{
                              color: 'rgba(255,255,255,0.5)',
                              padding: '2px',
                              '&:hover': { color: '#10B981', background: 'rgba(16,185,129,0.1)' },
                            }}
                          >
                            <ZoomInIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Box>
                      </Collapse>
                    </Box>
                  );
                })}
              </Box>
            </Box>

            {/* Zones Card for Tata Motors */}
            {projectDetails?.project_name?.toLowerCase().includes('tata') && (
              <Box
                className="glass-panel"
                sx={{
                  width: '100%',
                  padding: '16px',
                  borderRadius: isMapLayersExpanded ? '12px' : '24px',
                  background: 'rgba(10, 11, 14, 0.75)',
                  boxShadow: 'none',
                  border: '1px solid rgba(255,255,255,0.08)',
                  transform: isMapLayersExpanded ? 'translateY(0)' : 'translateY(-24px)',
                  opacity: isMapLayersExpanded ? 1 : 0,
                  transition: 'all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  overflow: 'hidden',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '10px',
                    fontWeight: 700,
                    color: 'rgba(255,255,255,0.5)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    display: 'flex',
                    alignItems: 'center',
                    mb: 1.5
                  }}
                >
                  <Layers sx={{ fontSize: 13, mr: 0.75, color: 'rgba(255,255,255,0.5)' }} />
                  Zones
                </Typography>

                <Box 
                  sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: 1.5, 
                    maxHeight: '200px', 
                    overflowY: 'auto', 
                    overflowX: 'hidden',
                    '&::-webkit-scrollbar': { display: 'none' }, 
                    msOverflowStyle: 'none',
                    scrollbarWidth: 'none',
                  }}
                >
                  {TATA_ZONES.map((zone) => {
                    const setting = zoneSettings[zone] || { visible: true, opacity: 0.15 };
                    return (
                      <Box key={zone} sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography sx={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.85)', fontWeight: 500 }}>
                            {zone}
                          </Typography>
                          <Switch
                            size="small"
                            checked={setting.visible}
                            onChange={(e) => setZoneSettings(prev => ({
                              ...prev,
                              [zone]: { ...setting, visible: e.target.checked }
                            }))}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': { color: TATA_ZONE_COLORS[zone] || '#10B981' },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: TATA_ZONE_COLORS[zone] || '#10B981' },
                            }}
                          />
                        </Box>
                        <Collapse in={setting.visible}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5 }}>
                            <Slider
                              size="small"
                              value={setting.opacity}
                              min={0}
                              max={1}
                              step={0.01}
                              onChange={(e, val) => setZoneSettings(prev => ({
                                ...prev,
                                [zone]: { ...setting, opacity: val as number }
                              }))}
                              sx={{
                                color: TATA_ZONE_COLORS[zone] || '#10B981',
                                ml: '-6px',
                                width: 'calc(100% - 24px)',
                                '& .MuiSlider-thumb': { width: 12, height: 12 },
                                '& .MuiSlider-track': { border: 'none' },
                              }}
                            />
                          </Box>
                        </Collapse>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}


            {/* Card 2: Devices Legend */}
            <Box
              className="glass-panel"
              sx={{
                width: '100%',
                padding: '16px',
                borderRadius: isMapLayersExpanded ? '12px' : '24px',
                background: 'rgba(10, 11, 14, 0.75)',
                boxShadow: 'none',
                border: '1px solid rgba(255,255,255,0.08)',
                transform: isMapLayersExpanded ? 'translateY(0)' : 'translateY(-24px)',
                opacity: isMapLayersExpanded ? 1 : 0,
                transition: 'all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
                overflow: 'hidden',
              }}
            >
              <Typography
                sx={{
                  fontSize: '10px',
                  fontWeight: 700,
                  color: 'rgba(255,255,255,0.5)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  mb: 1.5,
                  textAlign: 'left',
                }}
              >
                Monitoring Systems
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                
                {/* Visual Monitoring */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box 
                      sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
                      onClick={() => setIsVisualExpanded(!isVisualExpanded)}
                    >
                      {isVisualExpanded ? (
                        <KeyboardArrowDownIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.6)' }} />
                      ) : (
                        <KeyboardArrowRightIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.6)' }} />
                      )}
                      <Typography sx={{ fontSize: '12px', color: 'rgba(255,255,255,0.9)', fontWeight: 600, userSelect: 'none' }}>
                        Visual Monitoring
                      </Typography>
                    </Box>
                    <Switch
                      size="small"
                      checked={showCamera || showAquaticCamera}
                      onChange={(e) => {
                        setShowCamera(e.target.checked);
                        setShowAquaticCamera(e.target.checked);
                      }}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#10B981' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#10B981' },
                      }}
                    />
                  </Box>
                  <Collapse in={isVisualExpanded}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pl: 2, borderLeft: '1px solid rgba(255,255,255,0.1)', ml: 1, mt: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, opacity: availableDevices.camera > 0 ? 1 : 0.4 }}>
                          <CameraAltIcon sx={{ fontSize: 14, color: '#ef4444' }} />
                          <Typography sx={{ fontSize: '11px', color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}>
                            Terrestrial Cameras ({availableDevices.camera})
                          </Typography>
                        </Box>
                        <Switch 
                          size="small" 
                          checked={showCamera && availableDevices.camera > 0} 
                          disabled={availableDevices.camera === 0}
                          onChange={(e) => setShowCamera(e.target.checked)} 
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': { color: '#ef4444' },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#ef4444' },
                          }}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, opacity: availableDevices.aquaticCamera > 0 ? 1 : 0.4 }}>
                          <CameraAltIcon sx={{ fontSize: 14, color: '#3b82f6' }} />
                          <Typography sx={{ fontSize: '11px', color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}>
                            Aquatic Cameras ({availableDevices.aquaticCamera})
                          </Typography>
                        </Box>
                        <Switch 
                          size="small" 
                          checked={showAquaticCamera && availableDevices.aquaticCamera > 0} 
                          disabled={availableDevices.aquaticCamera === 0}
                          onChange={(e) => setShowAquaticCamera(e.target.checked)} 
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': { color: '#3b82f6' },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#3b82f6' },
                          }}
                        />
                      </Box>
                    </Box>
                  </Collapse>
                </Box>

                {/* Acoustic Monitoring */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box 
                      sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
                      onClick={() => setIsAcousticExpanded(!isAcousticExpanded)}
                    >
                      {isAcousticExpanded ? (
                        <KeyboardArrowDownIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.6)' }} />
                      ) : (
                        <KeyboardArrowRightIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.6)' }} />
                      )}
                      <Typography sx={{ fontSize: '12px', color: 'rgba(255,255,255,0.9)', fontWeight: 600, userSelect: 'none' }}>
                        Acoustic Monitoring
                      </Typography>
                    </Box>
                    <Switch
                      size="small"
                      checked={showBioacoustic || showHydroacoustic}
                      onChange={(e) => {
                        setShowBioacoustic(e.target.checked);
                        setShowHydroacoustic(e.target.checked);
                      }}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#10B981' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#10B981' },
                      }}
                    />
                  </Box>
                  <Collapse in={isAcousticExpanded}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pl: 2, borderLeft: '1px solid rgba(255,255,255,0.1)', ml: 1, mt: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, opacity: availableDevices.bioacoustic > 0 ? 1 : 0.4 }}>
                          <GraphicEqIcon sx={{ fontSize: 14, color: '#a855f7' }} />
                          <Typography sx={{ fontSize: '11px', color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}>
                            Terrestrial Recorders ({availableDevices.bioacoustic})
                          </Typography>
                        </Box>
                        <Switch 
                          size="small" 
                          checked={showBioacoustic && availableDevices.bioacoustic > 0} 
                          disabled={availableDevices.bioacoustic === 0}
                          onChange={(e) => setShowBioacoustic(e.target.checked)} 
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': { color: '#a855f7' },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#a855f7' },
                          }}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, opacity: availableDevices.hydroacoustic > 0 ? 1 : 0.4 }}>
                          <GraphicEqIcon sx={{ fontSize: 14, color: '#3b82f6' }} />
                          <Typography sx={{ fontSize: '11px', color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}>
                            Aquatic Recorders ({availableDevices.hydroacoustic})
                          </Typography>
                        </Box>
                        <Switch 
                          size="small" 
                          checked={showHydroacoustic && availableDevices.hydroacoustic > 0} 
                          disabled={availableDevices.hydroacoustic === 0}
                          onChange={(e) => setShowHydroacoustic(e.target.checked)} 
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': { color: '#3b82f6' },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#3b82f6' },
                          }}
                        />
                      </Box>
                    </Box>
                  </Collapse>
                </Box>

                {/* Soil Monitoring */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box 
                      sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
                      onClick={() => setIsSoilExpanded(!isSoilExpanded)}
                    >
                      {isSoilExpanded ? (
                        <KeyboardArrowDownIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.6)' }} />
                      ) : (
                        <KeyboardArrowRightIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.6)' }} />
                      )}
                      <Typography sx={{ fontSize: '12px', color: 'rgba(255,255,255,0.9)', fontWeight: 600, userSelect: 'none' }}>
                        Soil Monitoring
                      </Typography>
                    </Box>
                    <Switch
                      size="small"
                      checked={showSoilSensor}
                      onChange={(e) => setShowSoilSensor(e.target.checked)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#10B981' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#10B981' },
                      }}
                    />
                  </Box>
                  <Collapse in={isSoilExpanded}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pl: 2, borderLeft: '1px solid rgba(255,255,255,0.1)', ml: 1, mt: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, opacity: availableDevices.soilSensor > 0 ? 1 : 0.4 }}>
                          <GrassIcon sx={{ fontSize: 14, color: '#eab308' }} />
                          <Typography sx={{ fontSize: '11px', color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}>
                            Soil Samples ({availableDevices.soilSensor})
                          </Typography>
                        </Box>
                        <Switch 
                          size="small" 
                          checked={showSoilSensor && availableDevices.soilSensor > 0} 
                          disabled={availableDevices.soilSensor === 0}
                          onChange={(e) => setShowSoilSensor(e.target.checked)} 
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': { color: '#eab308' },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#eab308' },
                          }}
                        />
                      </Box>
                    </Box>
                  </Collapse>
                </Box>

              </Box>
            </Box>

            {/* Card 3: Deployment Cycle */}
            {projectDetails?.project_name?.toLowerCase().includes('tata') && (
              <Box
              className="glass-panel"
              sx={{
                width: '100%',
                padding: '16px',
                borderRadius: isMapLayersExpanded ? '12px' : '24px',
                background: 'rgba(10, 11, 14, 0.75)',
                boxShadow: 'none',
                border: '1px solid rgba(255,255,255,0.08)',
                transform: isMapLayersExpanded ? 'translateY(0)' : 'translateY(-24px)',
                opacity: isMapLayersExpanded ? 1 : 0,
                transition: 'all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
                overflow: 'hidden',
              }}
            >
              <Typography
                sx={{
                  fontSize: '10px',
                  fontWeight: 700,
                  color: 'rgba(255,255,255,0.5)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  display: 'flex',
                  alignItems: 'center',
                  mb: 1.5
                }}
              >
                <CalendarMonthIcon sx={{ fontSize: 13, mr: 0.75, color: 'rgba(255,255,255,0.5)' }} />
                Deployment Cycle
              </Typography>

              <FormControl size="small" variant="outlined" fullWidth>
                <Select
                  multiple
                  displayEmpty
                  value={deploymentCycles.includes('all') ? ['all', 'week1', 'week2', 'week3', 'week4'] : deploymentCycles}
                  onChange={(e) => {
                    const val = e.target.value as string[];
                    
                    const wasAllSelected = deploymentCycles.includes('all');
                    const isAllInVal = val.includes('all');
                    
                    if (wasAllSelected && !isAllInVal) {
                      setDeploymentCycles([]);
                    } else if (!wasAllSelected && isAllInVal) {
                      setDeploymentCycles(['all']);
                    } else {
                      const withoutAll = val.filter(v => v !== 'all');
                      if (withoutAll.includes('week1') && withoutAll.includes('week2') && withoutAll.includes('week3') && withoutAll.includes('week4')) {
                        setDeploymentCycles(['all']);
                      } else {
                        setDeploymentCycles(withoutAll);
                      }
                    }
                  }}
                  renderValue={(selected) => {
                    if (selected.includes('all')) return 'All';
                    if (selected.length === 0) return 'None';
                    const labels = [];
                    if (selected.includes('week1')) labels.push('Cycle 1');
                    if (selected.includes('week2')) labels.push('Cycle 2');
                    if (selected.includes('week3')) labels.push('Cycle 3');
                    if (selected.includes('week4')) labels.push('Cycle 4');
                    return labels.join(', ');
                  }}
                  sx={{
                    color: '#fff',
                    fontSize: '12px',
                    fontWeight: 600,
                    height: '32px',
                    background: 'rgba(255,255,255,0.05)',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255,255,255,0.1)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255,255,255,0.25)',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#10B981',
                    },
                    '& .MuiSelect-icon': {
                      color: '#10B981',
                    }
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        bgcolor: '#1a1f2b',
                        border: '1px solid rgba(255,255,255,0.1)',
                        '& .MuiMenuItem-root': {
                          fontSize: '12px',
                          color: 'rgba(255,255,255,0.85)',
                          padding: '4px 8px',
                          '&:hover': {
                            bgcolor: 'rgba(16,185,129,0.1)',
                          },
                          '&.Mui-selected': {
                            bgcolor: 'transparent',
                          },
                          '&.Mui-selected:hover': {
                            bgcolor: 'rgba(16,185,129,0.1)',
                          }
                        },
                        '& .MuiCheckbox-root': {
                          color: 'rgba(255,255,255,0.3)',
                          padding: '4px',
                          marginRight: '8px',
                          '&.Mui-checked': {
                            color: '#10B981',
                          }
                        },
                      }
                    }
                  }}
                >
                  <MenuItem value="all">
                    <Checkbox checked={deploymentCycles.includes('all')} size="small" />
                    <ListItemText primary="All" primaryTypographyProps={{ fontSize: '12px', fontWeight: 600 }} />
                  </MenuItem>
                  <MenuItem value="week1">
                    <Checkbox checked={deploymentCycles.includes('week1') || deploymentCycles.includes('all')} size="small" />
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>Cycle 1</Typography>
                      <Typography sx={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)' }}>12/08/26 - 21/08/26</Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="week2">
                    <Checkbox checked={deploymentCycles.includes('week2') || deploymentCycles.includes('all')} size="small" />
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>Cycle 2</Typography>
                      <Typography sx={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)' }}>21/09/26 - 28/09/26</Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="week3">
                    <Checkbox checked={deploymentCycles.includes('week3') || deploymentCycles.includes('all')} size="small" />
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>Cycle 3</Typography>
                      <Typography sx={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)' }}>01/10/26 - 07/10/26</Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="week4">
                    <Checkbox checked={deploymentCycles.includes('week4') || deploymentCycles.includes('all')} size="small" />
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>Cycle 4</Typography>
                      <Typography sx={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)' }}>12/10/26 - 19/10/26</Typography>
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>
            )}
          </Box>
        </Collapse>
      </Box>

      {/* 3D Map View Card for Tata Motors */}
      {isTataProject && (
        <Box
          sx={{
            position: 'absolute',
            top: 24,
            right: 24,
            zIndex: 10,
          }}
        >
          <Tata3DCard
            is3DActive={is3DActive}
            setIs3DActive={setIs3DActive}
            exaggeration={exaggeration}
            setExaggeration={setExaggeration}
            textureOpacity={textureOpacity}
            setTextureOpacity={setTextureOpacity}
            cursorElevation={cursorElevation}
            onFlyToSite={handleFlyToSite}
            onReset2D={handleReset2D}
          />
        </Box>
      )}
    </Box>
  );
};

export default InteractiveMap;
